import { DASHBOARD_ORIGIN } from "@/config/studio-env";
import { setHostSelectedTemplate } from "@/shared/lib/host-selection";

export { warnIfHostOriginDiffers } from "./hostOriginCheck";
export { installCanvasProbe } from "./canvasProbe";
export type { CanvasProbeRequest } from "./canvasProbe";

/** True when the Studio runs inside the dashboard's iframe. */
export function isEmbedded(): boolean {
  return window.parent !== window;
}

/** Class CSS branches on to tell embedded from standalone. */
export const EMBEDDED_HTML_CLASS = "is-embedded";

/**
 * Publish the embedded state to CSS by stamping `<html>`.
 *
 * Embedded, `body` stays transparent so the HOST's surface shows through —
 * the only way the two backgrounds can be guaranteed to match in every theme
 * without duplicating the token across the iframe boundary. Standalone has
 * nothing behind it, so `html:not(.is-embedded) body` paints the real surface
 * (see tailwind.scss).
 *
 * MUST run before React's first render — from main.tsx, never an effect. In
 * an effect the embedded document would paint opaque for a frame first, which
 * is exactly the seam at the iframe edge this removes.
 */
export function markEmbeddedOnHtml(): void {
  if (isEmbedded()) {
    document.documentElement.classList.add(EMBEDDED_HTML_CLASS);
  }
}

declare global {
  interface Window {
    /** Android's @JavascriptInterface, added under this exact name. */
    templateEditorLifecycle?: { postMessage: (message: string) => void };
  }
}

type NativeBridge = { postMessage: (message: unknown) => void };

/**
 * iOS registers its handler under `webkit.messageHandlers`. The auth layer
 * already declares that object for ITS handler, and TypeScript requires
 * duplicate global declarations to match exactly — so read ours through a
 * cast rather than redeclaring `webkit` here. `src/auth` is sealed, and a
 * second declaration of the same global is a drift waiting to happen.
 */
function iosLifecycleBridge(): NativeBridge | undefined {
  const handlers = window.webkit?.messageHandlers as
    | Record<string, NativeBridge | undefined>
    | undefined;
  return handlers?.templateEditorLifecycle;
}

/**
 * Send one lifecycle message to whichever host is listening.
 *
 * Three channels, native first — the same order and the same reason as
 * `detectHost()` in the auth layer: a WebView can also satisfy
 * `window.parent !== window` in some embedding setups, and if the iframe
 * branch won there every message would be posted at a parent with no
 * listener.
 *
 * Until 2026-08-25 there were only two branches — iframe, or drop — so a
 * native host received NOTHING: no content-ready, no error, no dirty/saved.
 * The mobile team hit exactly that (MOBILE_PREVIEW_HANDOFF.md §6) and would
 * have had to guess with onPageFinished plus a timer, which is the
 * double-loader bug the web host already fixed.
 *
 * The bridge is detected by ITS OWN handler, not by the auth one: a host
 * that implements the token handshake but not the lifecycle channel is a
 * legitimate state (it is what shipped first), and it must degrade to
 * silence rather than throwing.
 */
function post(message: Record<string, unknown>): void {
  const ios = iosLifecycleBridge();
  if (ios) {
    // A bridge throw must never take a caller with it: these are
    // notifications, and every one of them is fire-and-forget.
    try {
      ios.postMessage(message);
    } catch {
      /* native bridge went away mid-session */
    }
    return;
  }

  const android = window.templateEditorLifecycle;
  if (android) {
    try {
      // Android's @JavascriptInterface takes a string, exactly like the
      // auth bridge — the two must not disagree about that.
      android.postMessage(JSON.stringify(message));
    } catch {
      /* native bridge went away mid-session */
    }
    return;
  }

  // Standalone dev (no host at all) is a silent no-op — nothing to talk to,
  // and window.parent === window would just echo to ourselves.
  if (!isEmbedded()) return;
  // Explicit target origin, never "*": these messages describe editor state
  // and must only reach the dashboard that embeds us.
  window.parent.postMessage(message, DASHBOARD_ORIGIN);
}

/**
 * "Our context listener is live — send it again."
 *
 * The host posts its opening `studio:context` from the iframe's `load` event,
 * but `load` means the DOCUMENT finished, not that React mounted and installed
 * the listener. A context posted into that gap reaches nobody and is dropped
 * with no error anywhere — the same class of silent failure as the black-strip
 * bug, and the one the `?theme=` boot hint narrows but cannot close (it says
 * nothing about LOCALE).
 *
 * Post it AFTER installing the listener, or it re-creates the very race it
 * exists to remove. Safe to call repeatedly: the host answers idempotently,
 * and a host that predates this simply ignores it.
 */
export function notifyHostReady(): void {
  post({ type: "studio:ready" });
}

/**
 * Real content is on screen.
 *
 * NOT "React mounted" — that is `studio:ready`, which fires when the context
 * listener is installed. NOT "we have a height" — that is `studio:resize`,
 * which fires from a mount effect. Both arrive while this app is still showing
 * its OWN loading state, so a host that reveals on either drops its loader onto
 * a Studio that is itself still loading, or onto a document that has not
 * painted at all. The host reported exactly that: two loading screens in a row,
 * sometimes with a blank frame between them (2026-08-12).
 *
 * Posted on the transition into EVERY terminal state — content, empty, and
 * error alike. A surface that only announced its happy path would leave an
 * errored gallery sitting under the host's loader until their fallback timer
 * expired, which is a worse bug than the one this fixes.
 *
 * Idempotent, like `notifyHostReady`: the host ignores repeats, and a build
 * that never posts it keeps today's behaviour, so this lands incrementally.
 */
export const postContentReady = (): void =>
  post({ type: "studio:content-ready" });

export const postDirty = (dirty: boolean): void => post({ type: "studio:dirty", dirty });
export const postSaved = (): void => post({ type: "studio:saved" });
/**
 * Publish succeeded. Carries no payload by design: the store's own logo and
 * company name are synced SERVER-SIDE inside the publish endpoint (decided
 * 2026-08-06, option A in docs/studio/STORE_BRAND_SYNC_ON_PUBLISH.md), so no
 * client has to make a second, non-atomic write — and the mobile WebView host
 * gets the same behaviour without implementing anything.
 */
export const postPublished = (): void => post({ type: "studio:published" });
/** Where the HOST should land after it unmounts the editor. */
export type StudioCloseTarget = "templates" | "dashboard";

/**
 * Leave the editor. The host owns the navigation — the Studio never knows the
 * dashboard's route shape, which is the entire reason this message exists.
 *
 * `to` names the destination, and only the SENDER can know it: this one
 * message is posted from three places with two different intents (the
 * logo/Dashboard exit means the dashboard home; the breadcrumb and the route
 * error boundary mean the gallery). Re-pointing the message host-side would
 * have fixed one and broken the other.
 *
 * Omitted deliberately when the caller means the gallery — the key is then
 * ABSENT from the payload rather than sent as an explicit "templates", so the
 * host applies its own default. That also keeps this build working unchanged
 * against a host that predates the field.
 */
export const postClose = (to?: StudioCloseTarget): void =>
  post({ type: "studio:close", ...(to ? { to } : {}) });
// A request, not a notification: the host iframe sandbox has no
// allow-top-navigation, so the Studio asks the host to navigate the real page
// to its full-screen editor route (/templates/<templateVersionId>/editor).
export const postOpenEditor = (templateVersionId: string): void =>
  post({ type: "studio:open-editor", templateVersionId });
/**
 * Show this template FULL SCREEN, at the host's own
 * /templates/<templateVersionId>/preview route.
 *
 * The same request as `postOpenEditor` and for the same reason: the frame's
 * sandbox carries no allow-top-navigation, so a route that must escape the
 * gallery's slot has to be asked for. The modal preview is a letterboxed
 * device mock inside a dialog inside a frame; a merchant deciding between
 * templates wants the page at its real width, which only the host can give.
 *
 * Deliberately NOT `window.open`. The sandbox does allow popups, but a new
 * top-level tab of the Studio has no host to ask for a token — opened
 * directly it shows its terminal auth error by design — so the tab would
 * render nothing but a failure. The host's route embeds the Studio the same
 * way this gallery is embedded, which is what makes the token handshake work.
 *
 * A host that predates this message ignores it (its switch has no case), and
 * the button that sends it simply does nothing there — no error, no navigation.
 */
export const postOpenPreview = (templateVersionId: string): void =>
  post({ type: "studio:open-preview", templateVersionId });
/**
 * The merchant picked a template, in a host that is running the gallery as a
 * PICKER rather than as the themes page (`/templates?mode=select`).
 *
 * A notification, not a request: the Studio does not adopt, save, or navigate
 * in this mode — it reports the choice and the host decides what that means.
 * Onboarding's step 4 means "write TEMPLATE_SELECTION through the onboarding
 * API, with its own dirty-check, completion latch and diverged-state
 * recovery", none of which the Studio knows about and none of which should
 * move here.
 *
 * Carries `templateCode` and `name` beside the id because the host writes all
 * three into its step payload. The name is the backend's `templateName`,
 * unchanged, so the merchant meets the same name here as everywhere else.
 */
export const postTemplateSelected = (selection: {
  templateVersionId: string;
  templateCode: string;
  name: string;
}): void => post({ type: "studio:template-selected", ...selection });
// While open, the host expands the frame to cover the whole window
// (fixed inset-0 above all dashboard chrome, body scroll locked) so a modal
// centered in the Studio's viewport IS a full-window modal. Always post
// false when the overlaid surface goes away — never leave the host stuck.
export const postOverlay = (open: boolean): void =>
  post({ type: "studio:overlay", open });
// The inner document never scrolls inside the content-height iframe, so the
// page guide asks the HOST to smooth-scroll its window until the element
// (at `top` document-offset, `height` px tall) is centered.
export const postScrollTo = (top: number, height: number): void =>
  post({ type: "studio:scroll-to", top, height });
// The page guide's own mask can only dim the frame area. While `on`, the
// host renders veil strips around the frame rect so the dim runs
// wall-to-wall over the dashboard chrome — WITHOUT moving or resizing the
// frame (that was the rejected studio:overlay approach). The strips mirror
// the spotlight mask's rgba exactly; see TourSpotlight's mask comment.
export const postDim = (on: boolean): void => post({ type: "studio:dim", on });
export const postResize = (height: number): void =>
  post({ type: "studio:resize", height });

/**
 * Why a surface cannot continue. The host's vocabulary (host team,
 * 2026-08-13) — it reacts differently to each, so the SENDER has to name
 * which one it is:
 *
 *   · SESSION_EXPIRED — the editor token could not be minted or was
 *     rejected. The host prompts re-auth and keeps the merchant on the route.
 *   · RENDER_ERROR    — a render threw. Error state plus a retry that
 *     reloads the frame.
 *   · LOAD_FAILED     — a required fetch failed and there is no way
 *     forward in-frame. Error state plus retry.
 *   · NOT_FOUND       — the surface was asked for something that does not
 *     exist, or asked for nothing at all. Error state plus a way back to
 *     the gallery.
 */
export type StudioErrorCode =
  | "SESSION_EXPIRED"
  | "RENDER_ERROR"
  | "LOAD_FAILED"
  | "NOT_FOUND";

/**
 * "I have failed and cannot continue."
 *
 * Deliberately NOT studio:close. Close means the merchant is finished with
 * the surface, so the host navigates — and now that the dashboard's legacy
 * template routes are gone there is nothing behind us to navigate to. A
 * gallery that failed and asked to be closed would be routed straight back
 * to itself. Only the Studio knows which of the two happened, which is the
 * whole reason this is a second message rather than a field on the first
 * (same argument as `to` on postClose).
 *
 * `message` is diagnostics only — never merchant-facing copy. The host
 * renders its own, because this text reaches merchants in en/ne/hi and the
 * Studio's strings are not the host's to translate.
 *
 * Surfaces post this ALONGSIDE studio:content-ready, not instead of it: a
 * host that predates this message still drops its loader and shows the
 * Studio's own error screen, which is exactly today's behaviour.
 */
export const postError = (
  code: StudioErrorCode,
  message?: string,
): void =>
  post({ type: "studio:error", code, ...(message ? { message } : {}) });

/**
 * Report the document's content height to the host whenever it changes, so
 * the dashboard sizes the iframe to fit and the page scrolls with the
 * DASHBOARD's scrollbar — never an inner iframe one. Document-flow surfaces
 * only (the gallery); viewport-app surfaces (the editor) size themselves.
 *
 * ## Why this does not read `documentElement.scrollHeight`
 *
 * It used to, and the frame could then only ever GROW. `src/styles/fonts.scss`
 * pins `html, body, #root { height: 100% }` — app-shell sizing, applied
 * embedded as well — so `scrollHeight` is `max(content, viewport)`, and the
 * viewport IS the frame the host already gave us. Report it back and the frame
 * keeps the height it has: a ratchet, converging upward and never down.
 *
 * Nothing was wrong with it while the gallery was always taller than its
 * frame. It showed up the moment a surface got SHORTER than one — onboarding's
 * theme step, which opened tall and then kept ~600px of dead space under the
 * cards that the merchant had to scroll past (Milan, 2026-09-18).
 *
 * Measuring the in-flow children of `#root` answers the question actually
 * being asked — how tall is the content — and is unaffected by whatever height
 * the shell is pinned to. Overlays are skipped: a `fixed` child measures the
 * viewport, which would be the same ratchet wearing a different hat, and a
 * modal must not grow the host's frame (that is `studio:overlay`'s job).
 *
 * The same pinning is why the children are observed and not just the document:
 * with `html` at 100%, `documentElement` and `body` only ever resize when the
 * FRAME does, so observing them alone means content changes are never noticed.
 */
const CONTENT_ROOT_ID = "root";

function measureContentHeight(): number {
  const root = document.getElementById(CONTENT_ROOT_ID);
  const fallback = () => Math.ceil(document.documentElement.scrollHeight);
  if (!root) return fallback();

  let bottom = 0;
  for (const child of Array.from(root.children)) {
    // Computed first — real positioning comes from a class, not an inline
    // style — with the inline value as a fallback, because `getComputedStyle`
    // is not always there to answer: jsdom returns `undefined` for `position`,
    // and a detached element can come back with an empty declaration in a
    // browser too. Without the fallback this guard is silently inert rather
    // than wrong, which is the harder kind of bug to notice.
    const position =
      window.getComputedStyle(child).position ||
      (child as HTMLElement).style?.position;
    if (position === "fixed" || position === "absolute") continue;
    bottom = Math.max(bottom, child.getBoundingClientRect().bottom);
  }
  // Rects are viewport-relative; the inner document should never be scrolled
  // once the frame fits it, but it can be mid-settle.
  bottom += window.scrollY;
  // Nothing in flow (every child an overlay, or an empty root mid-render):
  // the old measurement is wrong-but-safe, where 0 would collapse the frame.
  return bottom > 0 ? Math.ceil(bottom) : fallback();
}

export function installContentHeightReporter(): () => void {
  if (!isEmbedded() || typeof ResizeObserver === "undefined") return () => {};

  let lastReported = -1;
  const report = () => {
    const height = measureContentHeight();
    if (height === lastReported) return;
    lastReported = height;
    postResize(height);
  };

  const observer = new ResizeObserver(report);
  observer.observe(document.documentElement);
  observer.observe(document.body);

  // Watch what actually changes with the content — see the doc above.
  const watched = new WeakSet<Element>();
  const watchChildren = () => {
    const root = document.getElementById(CONTENT_ROOT_ID);
    if (!root) return;
    for (const child of Array.from(root.children)) {
      if (watched.has(child)) continue;
      watched.add(child);
      observer.observe(child);
    }
  };

  // React replaces `#root`'s children on every route change, and a fresh one
  // is unobserved until it is watched.
  const mutations =
    typeof MutationObserver === "undefined"
      ? null
      : new MutationObserver(() => {
          watchChildren();
          report();
        });
  const root = document.getElementById(CONTENT_ROOT_ID);
  if (root && mutations) mutations.observe(root, { childList: true });

  watchChildren();
  report();

  return () => {
    observer.disconnect();
    mutations?.disconnect();
  };
}

export interface HostContextHandlers {
  onTheme?: (theme: "light" | "dark") => void;
  onLocale?: (locale: string) => void;
}

/**
 * Listen for the host's studio:context (posted by TemplateStudioFrame's
 * onLoad): boot theme + locale, so the editor never paints in the wrong
 * theme before correcting itself. Origin check comes before any event.data
 * read, exactly like the auth channel.
 */
export function installHostContextListener(handlers: HostContextHandlers): () => void {
  const onMessage = (event: MessageEvent) => {
    if (event.origin !== DASHBOARD_ORIGIN) return;
    const data = event.data as { type?: unknown; theme?: unknown; locale?: unknown } | null;
    if (!data || typeof data !== "object" || data.type !== "studio:context") return;
    if (data.theme === "light" || data.theme === "dark") handlers.onTheme?.(data.theme);
    if (typeof data.locale === "string" && data.locale) handlers.onLocale?.(data.locale);
    const selected = (data as { selectedTemplateVersionId?: unknown })
      .selectedTemplateVersionId;
    setHostSelectedTemplate(
      typeof selected === "string" && selected ? selected : undefined,
    );
  };
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}
